'use client';

import { useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { JartinetHermandad } from '@/lib/types';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Calendar, AlignLeft, Users, Church, Info, MapPin, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import Image from '@/components/Image';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PrimitivaIcon } from '@/components/icons/PrimitivaIcon';
import { PontificiaIcon } from '@/components/icons/PontificiaIcon';
import { SacramentalIcon } from '@/components/icons/SacramentalIcon';

const DEFAULT_IMAGE = 'https://i.imgur.com/kYkL52G.png';

function hexToRgba(hex: string, alpha: number): string {
  if (!hex || !hex.startsWith('#') || hex.length < 7) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function StatCard({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-2xl bg-muted/40 p-4 text-center">
      <Icon className="h-5 w-5 text-primary" />
      <span className="font-headline text-base font-bold leading-tight">{value}</span>
      <span className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">{label}</span>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 rounded-2xl bg-muted/40 p-4">
      <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground" />
      <div>
        <h4 className="text-sm font-semibold">{label}</h4>
        <p className="text-sm text-muted-foreground">{value}</p>
      </div>
    </div>
  );
}

function HermandadCard({ hermandad, onClick }: { hermandad: JartinetHermandad; onClick: () => void }) {
  const color = hermandad.colorPrimario || '#9b8854';
  const colorSec = hermandad.colorSecundario || color;
  const fotoUrl = hermandad.foto || DEFAULT_IMAGE;
  const memberCount = hermandad.hermanos?.length || 0;
  const isPrimitiva = hermandad.titulos?.some((t) => t.toLowerCase() === 'primitiva');
  const isPontificia = hermandad.titulos?.some((t) => t.toLowerCase() === 'pontificia');
  const isSacramental = hermandad.titulos?.some((t) => t.toLowerCase() === 'sacramental');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      whileHover={{ y: -6 }}
      whileTap={{ scale: 0.97 }}
      transition={{ duration: 0.4 }}
      className="group relative cursor-pointer"
      onClick={onClick}
    >
      <div
        className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/8 shadow-md transition-all duration-300 group-hover:border-white/20 group-hover:shadow-[0_8px_32px_rgba(0,0,0,0.35)]"
        style={{ background: `linear-gradient(145deg, ${hexToRgba(color, 0.18)} 0%, hsl(var(--card)) 55%)` }}
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[3px] rounded-t-2xl opacity-80 transition-opacity duration-300 group-hover:opacity-100"
          style={{ background: `linear-gradient(90deg, ${color}, ${colorSec})` }}
        />

        <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-2xl">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-15 transition-transform duration-700 ease-out group-hover:scale-110 group-hover:opacity-25"
            style={{ backgroundImage: `url('${fotoUrl}')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/60 to-transparent" />
        </div>

        {(isPrimitiva || isPontificia || isSacramental) && (
          <div className="absolute right-3 top-3 z-10 flex gap-1.5">
            <TooltipProvider delayDuration={200}>
              {isPrimitiva && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="rounded-full border border-white/10 bg-background/75 p-1.5 shadow-sm backdrop-blur-md">
                      <div className="h-3.5 w-3.5">
                        <PrimitivaIcon />
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">Título de Primitiva Hermandad</TooltipContent>
                </Tooltip>
              )}
              {isPontificia && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="rounded-full border border-white/10 bg-background/75 p-1.5 shadow-sm backdrop-blur-md">
                      <div className="h-3.5 w-3.5">
                        <PontificiaIcon />
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">Título de Hermandad Pontificia</TooltipContent>
                </Tooltip>
              )}
              {isSacramental && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="rounded-full border border-white/10 bg-background/75 p-1.5 shadow-sm backdrop-blur-md">
                      <div className="h-3.5 w-3.5">
                        <SacramentalIcon />
                      </div>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs">Título de Hermandad Sacramental</TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </div>
        )}

        <div className="relative z-10 flex flex-1 flex-col items-center gap-3 p-5 pt-6">
          <div className="relative flex-shrink-0">
            <div
              className="pointer-events-none absolute inset-0 scale-[1.8] rounded-full opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-40"
              style={{ backgroundColor: color }}
            />
            <Image
              src={hermandad.escudoUrl}
              alt={`Escudo de ${hermandad.nombre}`}
              className="relative z-10 h-[76px] w-[76px] object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] transition-transform duration-400 group-hover:-translate-y-1"
            />
          </div>

          <div className="flex flex-1 flex-col items-center justify-center gap-0.5 text-center">
            <h3 className="font-headline text-[0.95rem] font-bold leading-tight transition-colors duration-300 group-hover:text-primary line-clamp-2">
              {hermandad.nombre}
            </h3>
            {hermandad.sede && <p className="mt-0.5 line-clamp-1 text-[0.7rem] text-muted-foreground/60">{hermandad.sede}</p>}
          </div>

          <div className="mt-1 flex w-full flex-col items-center gap-2">
            <div
              className="h-px w-full opacity-20 transition-opacity duration-300 group-hover:opacity-40"
              style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
            />
            <div className="flex items-center gap-1.5 text-[0.7rem] text-muted-foreground/70">
              <Users className="h-3 w-3 flex-shrink-0" />
              <span>
                {memberCount} {memberCount === 1 ? 'hermano' : 'hermanos'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function PublicHermandadesClient({ initialHermandades }: { initialHermandades: JartinetHermandad[] }) {
  const [selectedHermandad, setSelectedHermandad] = useState<JartinetHermandad | null>(null);

  const hermandadesDePenitencia = initialHermandades.filter((h) => h.tipo === 'Hermandad');
  const hermandadesDeGloria = initialHermandades.filter((h) => h.tipo === 'Hermandad de Gloria');
  const proHermandades = initialHermandades.filter((h) => h.tipo === 'Pro-Hermandad');
  const gruposParroquiales = initialHermandades.filter((h) => h.tipo === 'Grupo Parroquial');

  const fotoUrl = selectedHermandad?.foto || DEFAULT_IMAGE;
  const color = selectedHermandad?.colorPrimario || '#9b8854';
  const colorSec = selectedHermandad?.colorSecundario || color;

  const tabs = useMemo(
    () => [{ id: 'info', label: 'Info', icon: Info }],
    []
  );
  const [activeTab, setActiveTab] = useState<'info'>('info');

  const renderGroup = (title: string, hermandades: JartinetHermandad[]) => {
    if (hermandades.length === 0) return null;
    return (
      <div key={title}>
        <div className="mb-8 flex items-center gap-4">
          <h2 className="font-headline text-3xl font-bold md:text-4xl">{title}</h2>
          <div className="h-px flex-1 bg-gradient-to-r from-primary/40 to-transparent" />
        </div>
        <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {hermandades.map((hermandad) => (
            <HermandadCard
              key={hermandad.id}
              hermandad={hermandad}
              onClick={() => {
                setSelectedHermandad(hermandad);
                setActiveTab('info');
              }}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <section className="relative overflow-hidden border-b border-white/8 py-20 text-center">
        <div className="absolute -top-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-primary/10 blur-[120px]" />
        <div className="container relative mx-auto max-w-3xl px-4">
          <motion.h1
            className="font-headline text-5xl font-bold md:text-7xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent">Nuestras Hermandades</span>
          </motion.h1>
          <motion.p
            className="mt-4 text-base text-muted-foreground md:text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Cada hermandad en Jartiland tiene su propia historia, sus propias metas y su propio honor. Descubre a cuál perteneces o inspírate para crear la tuya.
          </motion.p>
        </div>
      </section>

      <section className="container mx-auto space-y-16 px-4 py-16">
        {renderGroup('Hermandades de Penitencia', hermandadesDePenitencia)}
        {renderGroup('Hermandades de Gloria', hermandadesDeGloria)}
        {renderGroup('Pro-Hermandades', proHermandades)}
        {renderGroup('Grupos Parroquiales', gruposParroquiales)}
      </section>

      <Sheet open={!!selectedHermandad} onOpenChange={(open) => !open && setSelectedHermandad(null)}>
        <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
          {selectedHermandad && (
            <>
              <div className="relative flex min-h-[220px] flex-shrink-0 flex-col justify-end overflow-hidden">
                <div className="absolute inset-0">
                  <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${fotoUrl}')` }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
                  <div
                    className="absolute inset-0 opacity-30"
                    style={{ background: `linear-gradient(145deg, ${color} 0%, transparent 55%)` }}
                  />
                </div>

                <div className="relative z-10 flex flex-col items-center gap-3 px-6 pb-5 pt-10 text-center">
                  <div className="relative">
                    <div
                      className="absolute inset-0 scale-[2.2] rounded-full opacity-35 blur-2xl"
                      style={{ backgroundColor: color }}
                    />
                    <div
                      className="relative rounded-full border-2 p-2.5 backdrop-blur-sm"
                      style={{ borderColor: hexToRgba(color, 0.45), backgroundColor: hexToRgba(color, 0.2) }}
                    >
                      <Image
                        src={selectedHermandad.escudoUrl}
                        alt={`Escudo de ${selectedHermandad.nombre}`}
                        className="h-[88px] w-[88px] object-contain drop-shadow-[0_4px_20px_rgba(0,0,0,0.7)]"
                      />
                    </div>
                  </div>
                  <SheetHeader className="m-0 p-0">
                    <SheetTitle className="font-headline text-xl font-bold">{selectedHermandad.nombreCompleto}</SheetTitle>
                    <SheetDescription className="text-sm text-muted-foreground/70">{selectedHermandad.nombre}</SheetDescription>
                  </SheetHeader>
                  <div className="flex flex-wrap justify-center gap-1.5">
                    <span
                      className="rounded-full border px-2.5 py-0.5 text-[0.68rem] font-medium backdrop-blur-sm"
                      style={{ borderColor: hexToRgba(color, 0.5), backgroundColor: hexToRgba(color, 0.25) }}
                    >
                      {selectedHermandad.tipo}
                    </span>
                    {selectedHermandad.dia && (
                      <span
                        className="rounded-full border px-2.5 py-0.5 text-[0.68rem] font-medium backdrop-blur-sm"
                        style={{ borderColor: hexToRgba(colorSec, 0.5), backgroundColor: hexToRgba(colorSec, 0.25) }}
                      >
                        {selectedHermandad.dia}
                      </span>
                    )}
                  </div>
                </div>

                <div className="relative z-10 flex h-[3px] flex-shrink-0">
                  <div style={{ backgroundColor: color, flex: 1 }} />
                  <div style={{ backgroundColor: colorSec, flex: 1 }} />
                </div>
              </div>

              <div className="flex flex-shrink-0 items-center gap-2 border-b border-white/5 bg-background/60 px-3 py-2 backdrop-blur-sm">
                <TooltipProvider delayDuration={300}>
                  <div className="flex gap-0.5">
                    {tabs.map(({ id, label, icon: Icon }) => {
                      const isActive = activeTab === id;
                      return (
                        <Tooltip key={id}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => setActiveTab(id as 'info')}
                              className={cn(
                                'flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-200',
                                isActive ? 'text-white shadow-md' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                              )}
                              style={isActive ? { backgroundColor: color } : {}}
                            >
                              <Icon className="h-4 w-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="text-xs">
                            {label}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </TooltipProvider>

                {/* Ficha pública completa de la hermandad */}
                <Link
                  to={`/hermandades/${selectedHermandad.id}`}
                  className="ml-auto flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-xs font-semibold transition-colors hover:bg-white/10"
                  style={{ color }}
                >
                  Ver ficha completa
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              <ScrollArea className="relative flex-1">
                <div
                  className="absolute inset-0 opacity-[0.04]"
                  style={{ backgroundImage: `url(${selectedHermandad.escudoUrl})`, backgroundSize: '120px', backgroundRepeat: 'repeat' }}
                />
                <div className="relative z-10 space-y-6 p-6">
                  <div className="grid grid-cols-3 gap-3">
                    <StatCard icon={Calendar} label="Fundación" value={selectedHermandad.fundacion} />
                    <StatCard icon={Users} label="Hermanos" value={selectedHermandad.hermanos?.length || 0} />
                    <StatCard icon={Church} label="Tipo" value={selectedHermandad.tipo} />
                  </div>

                  <div className="space-y-3">
                    <InfoRow icon={MapPin} label="Sede Canónica" value={selectedHermandad.sede} />
                    <InfoRow icon={Church} label="Titulares" value={selectedHermandad.titulares} />
                  </div>

                  {selectedHermandad.titulos && selectedHermandad.titulos.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selectedHermandad.titulos.map((titulo) => (
                        <span
                          key={titulo}
                          className="rounded-full border px-2.5 py-1 text-xs font-medium"
                          style={{ borderColor: hexToRgba(color, 0.4), backgroundColor: hexToRgba(color, 0.12) }}
                        >
                          {titulo}
                        </span>
                      ))}
                    </div>
                  )}

                  {selectedHermandad.info && (
                    <>
                      <div className="flex h-[2px] w-full overflow-hidden rounded-full">
                        <div style={{ backgroundColor: color }} className="flex-1" />
                        <div style={{ backgroundColor: colorSec }} className="flex-1" />
                      </div>
                      <div className="flex items-start gap-3">
                        <AlignLeft className="mt-1 h-5 w-5 flex-shrink-0 text-muted-foreground" />
                        <div className="prose prose-sm prose-invert max-w-none text-muted-foreground">
                          <ReactMarkdown>{selectedHermandad.info}</ReactMarkdown>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </ScrollArea>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
